import React from 'react';
import {
  FaCog,
  FaEdit,
  FaImage,
  FaLock,
  FaLockOpen,
  FaPalette,
  FaMoneyBillWaveAlt,
  FaTimes,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { update, updateField } from '../../../services/item.js';
import { setOpenModales } from '../../../store/global.js';
import { deepCloneArray } from '../../../utils/array.js';
import { getData, getId, getPicEntryById } from '../../../utils/item.js';
import { isEditable } from '../../../utils/roles.js';
import { GetField } from '../../field/field.Get.js';
import { TooltipHtml } from '../../tooltip/tooltip.Html.js';
import { ColorsSwatch } from '../../form/colorsSwatch.js';
import { OpacitySlider } from '../../form/slider.Opacity.js';
import { PicSelectionOptions } from '../../pic/pic.Selector.js';
import { Checkbox } from '../../form/checkbox.js';
import { hasDynamicHeight } from '../../../utils/gridLayout.js';

const BoardGridMapComponent = props => {
  const {
    element,
    tab,
    editable,
    widgetEditable = editable,
    getTextHeight,
    gridLayoutKey,
    type,
    boardWidth,
    rowHeight,
    cols,
    showDrawLayerControls = false,
    onSetDrawLayer,
  } = props;
  const isDrawBoard = type === 'draw';
  const dispatch = useDispatch();
  const elementObj = getData(element);
  const isMobile = useSelector(state => state.global.isMobile);
  const ownerItem = useSelector(state => state.items?.[elementObj?.item]);

  const removeElement = id => {
    const clonedGrid = deepCloneArray(tab.grid);
    let idx = -1;
    for (let i = 0; i < clonedGrid.length; i++) {
      const element = clonedGrid[i];
      if (getId(element) === id || getId(element) === 'undefined') {
        idx = i;
      }
    }

    if (idx > -1) {
      clonedGrid.splice(idx, 1);
      update(`items/${tab._id}`, 'grid', clonedGrid);
    }
  };

  // const updateField = (value, id, field) => {
  //   const clonedGrid = deepCloneArray(tab.grid);
  //   let idx = -1;
  //   for (let i = 0; i < clonedGrid.length; i++) {
  //     const element = clonedGrid[i];
  //     if (getId(element) === id) {
  //       idx = i;
  //     }
  //   }
  //   if (idx > -1) {
  //     if (type === 'map') {
  //       const map = clonedGrid[idx][id]?.map ?? {};
  //       clonedGrid[idx][id] = { ...clonedGrid[idx][id], ...map };
  //       clonedGrid[idx][id].map = {
  //         ...map,
  //         [field]: field === 'value' ? +value : value,
  //       };
  //     } else {
  //       clonedGrid[idx][id][field] = field === 'value' ? +value : value;
  //     }
  //     if (value === 'hauteur fixe') {
  //       clonedGrid[idx][id].isResizable = true;
  //     }
  //     if (value === 'hauteur dynamique') {
  //       clonedGrid[idx][id].isResizable = false;
  //     }
  //     update(`items/${tab._id}`, 'grid', clonedGrid);
  //   }
  // };

  let displayTypes = null;
  if (elementObj?.type === 'num' || elementObj?.type === 'has_num') {
    displayTypes = [
      'big',
      'big slash',
      'little',
      'jauge',
      'jauge vertical',
      'little jauge',
      'little jauge vertical',
    ];
  }
  if (elementObj?.type === 'conditions') {
    displayTypes = ['big', 'little', 'rond', 'checkbox'];
  }
  if (elementObj?.type === 'pics') {
    displayTypes = ['pleine largeur', 'arrondi', 'rond'];
  }
  if (elementObj?.type === 'text') {
    displayTypes = ['hauteur dynamique', 'hauteur fixe', 'label'];
  }
  if (elementObj?.type === 'link') {
    displayTypes = [
      'simple',
      'expandable',
      'réduit',
      'complet',
      'complet dynamique',
    ];
  }
  if (isDrawBoard && elementObj?.type === 'text') {
    displayTypes = displayTypes?.filter(dt => dt !== 'hauteur dynamique');
  }
  if (isDrawBoard && elementObj?.type === 'link') {
    displayTypes = displayTypes?.filter(dt => dt !== 'complet dynamique');
  }
  if (elementObj?.type === 'divider') {
    displayTypes = ['simple', 'dashed', 'none'];
  }
  if (elementObj?.type === 'filters') {
    displayTypes = [
      'simple',
      'réduit',
      'complet',
      'massonery',
      'timeline',
      'timeline vertical',
    ];
  }
  if (elementObj?.type === 'timeline') {
    displayTypes = ['horizontal', 'vertical'];
  }
  if (elementObj?.type === 'dice') {
    // Internally we used to store `simple`. Display label should be clear for non-CSS users.
    displayTypes = ['historique', 'dernier resultat'];
  }

  const rawDisplayType = elementObj?.displayType;
  const normalizedDisplayType =
    isDrawBoard && rawDisplayType === 'hauteur dynamique'
      ? 'hauteur fixe'
      : isDrawBoard && rawDisplayType === 'complet dynamique'
        ? 'complet'
        : rawDisplayType;
  const displayType =
    elementObj?.type === 'dice' && normalizedDisplayType === 'simple'
      ? 'historique'
      : normalizedDisplayType || displayTypes?.[0] || null;

  const displayCases = ['case pleine largeur', 'case arrondie', 'masque'];

  const [bgOpacityPreview, setBgOpacityPreview] = React.useState(null);

  const bgPicEntry = getPicEntryById(ownerItem?.pics, elementObj?.bgPicId);
  const bgPicUrl = bgPicEntry?.url || null;
  const bgOpacity =
    typeof elementObj?.bgPicOpacity === 'number'
      ? elementObj.bgPicOpacity
      : 0.35;
  const bgPicFit = elementObj?.bgPicFit || 'content';
  // `fixed` is treated as a "continuous background" mode (aligned to the grid),
  // because `background-attachment: fixed` is unreliable in transformed grids.
  const bgPicSync = elementObj?.bgPicSync === true || bgPicFit === 'fixed';
  const effectiveBgOpacity =
    typeof bgOpacityPreview === 'number' ? bgOpacityPreview : bgOpacity;

  const bgMaskPicEntry = getPicEntryById(
    ownerItem?.pics,
    elementObj?.bgMaskPicId,
  );
  const bgMaskUrl = bgMaskPicEntry?.url || null;
  const bgMaskFit = elementObj?.bgMaskFit || 'contain';
  const bgMaskSync = bgMaskFit === 'fixed';
  const bgMaskInvert = elementObj?.bgMaskInvert === true;
  const bgMaskRotate = Number.isFinite(elementObj?.bgMaskRotate)
    ? elementObj.bgMaskRotate
    : 0;

  React.useEffect(() => {
    setBgOpacityPreview(null);
  }, [elementObj?.bgPicOpacity, elementObj?.bgPicId, elementObj?.i]);

  const bgPicOptions = React.useMemo(() => {
    const options = [{ key: 'none', label: 'Aucun fond' }];
    (ownerItem?.pics || []).forEach((picObj, picIndex) => {
      const picId = getId(picObj);
      const payload = picObj?.[picId] || {};
      options.push({
        key: picId,
        label: payload?.label || `Image ${picIndex + 1}`,
        previewUrl: payload?.url || null,
        iconSlug: payload?.iconSlug || null,
      });
    });
    return options;
  }, [ownerItem?.pics]);

  const maskPicOptions = React.useMemo(() => {
    const options = [{ key: 'none', label: 'Aucun masque' }];
    (ownerItem?.pics || []).forEach((picObj, picIndex) => {
      const picId = getId(picObj);
      const payload = picObj?.[picId] || {};
      if (!payload?.url) {
        return;
      }
      options.push({
        key: picId,
        label: payload?.label || `Image ${picIndex + 1}`,
        previewUrl: payload?.url || null,
        iconSlug: null,
      });
    });
    return options;
  }, [ownerItem?.pics]);

  const gridMetrics = React.useMemo(() => {
    const safeCols = Number.isFinite(cols) && cols > 0 ? cols : 12;
    const safeWidth =
      Number.isFinite(boardWidth) && boardWidth > 0 ? boardWidth : 0;
    const safeRowH =
      Number.isFinite(rowHeight) && rowHeight > 0 ? rowHeight : 30;

    let maxRows = 1;
    if (Array.isArray(tab?.grid)) {
      for (const gridObj of tab.grid) {
        const gridElement = getData(gridObj);
        if (!gridElement) continue;
        const endRow =
          (Number.isFinite(gridElement.y) ? gridElement.y : 0) +
          (Number.isFinite(gridElement.h) ? gridElement.h : 0);
        if (endRow > maxRows) {
          maxRows = endRow;
        }
      }
    }

    return {
      colW: safeWidth ? safeWidth / safeCols : 0,
      rowH: safeRowH,
      gridW: safeWidth,
      gridH: maxRows * safeRowH,
    };
  }, [tab?.grid, boardWidth, rowHeight, cols]);

  const shouldUseMask =
    elementObj?.displayCase === 'masque' && Boolean(bgMaskUrl);

  const rotatedMaskCacheRef = React.useRef(new Map());
  const [rotatedMaskUrl, setRotatedMaskUrl] = React.useState(null);

  React.useEffect(() => {
    if (!shouldUseMask || !bgMaskUrl || !bgMaskRotate) {
      setRotatedMaskUrl(null);
      return;
    }

    const key = `${bgMaskUrl}|${bgMaskRotate}`;
    const cached = rotatedMaskCacheRef.current.get(key);
    if (cached) {
      setRotatedMaskUrl(cached);
      return;
    }

    let cancelled = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    const rotate = async () => {
      try {
        const normalized = ((bgMaskRotate % 360) + 360) % 360;
        if (!normalized) {
          setRotatedMaskUrl(null);
          return;
        }

        const res = await fetch(bgMaskUrl, {
          mode: 'cors',
          signal: controller?.signal,
        });
        if (!res.ok) {
          return;
        }
        const blob = await res.blob();

        const loadImage = async () => {
          if (typeof createImageBitmap === 'function') {
            try {
              return await createImageBitmap(blob);
            } catch (e) {
              // fallback below
            }
          }
          const objectUrl = URL.createObjectURL(blob);
          try {
            const img = new Image();
            img.decoding = 'async';
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = objectUrl;
            });
            return img;
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        };

        const img = await loadImage();
        if (!img || cancelled) return;

        const w = img.width || img.naturalWidth || 0;
        const h = img.height || img.naturalHeight || 0;
        if (!w || !h) return;

        const radians = (normalized * Math.PI) / 180;
        const isQuarterTurn = normalized === 90 || normalized === 270;
        const canvas = document.createElement('canvas');
        canvas.width = isQuarterTurn ? h : w;
        canvas.height = isQuarterTurn ? w : h;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(radians);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);

        const dataUrl = canvas.toDataURL('image/png');
        rotatedMaskCacheRef.current.set(key, dataUrl);
        if (!cancelled) {
          setRotatedMaskUrl(dataUrl);
        }
      } catch (e) {
        // ignore and fallback to non-rotated mask
      }
    };

    rotate();
    return () => {
      cancelled = true;
      controller?.abort?.();
    };
  }, [shouldUseMask, bgMaskUrl, bgMaskRotate]);

  const maskStyle = React.useMemo(() => {
    if (!shouldUseMask) {
      return null;
    }

    const size =
      bgMaskSync && gridMetrics.gridW > 0 && gridMetrics.gridH > 0
        ? `${gridMetrics.gridW}px ${gridMetrics.gridH}px`
        : bgMaskFit === 'stretch'
          ? '100% 100%'
        : bgMaskFit === 'contain'
          ? 'contain'
          : 'cover';
    const pos = bgMaskSync
      ? `${-(elementObj?.x ?? 0) * gridMetrics.colW}px ${
          -(elementObj?.y ?? 0) * gridMetrics.rowH
        }px`
      : 'center';

    const baseMaskSource =
      bgMaskRotate && rotatedMaskUrl ? rotatedMaskUrl : bgMaskUrl;
    const baseMaskImage = baseMaskSource ? `url("${baseMaskSource}")` : null;
    const fullMaskImage = 'linear-gradient(#fff, #fff)';
    const maskImage = bgMaskInvert
      ? `${baseMaskImage}, ${fullMaskImage}`
      : baseMaskImage;

    return {
      // Luminance masks allow black/white images to work without alpha.
      WebkitMaskImage: maskImage,
      maskImage: maskImage,
      WebkitMaskRepeat: bgMaskInvert ? 'no-repeat, no-repeat' : 'no-repeat',
      maskRepeat: bgMaskInvert ? 'no-repeat, no-repeat' : 'no-repeat',
      WebkitMaskMode: bgMaskInvert ? 'luminance, luminance' : 'luminance',
      maskMode: bgMaskInvert ? 'luminance, luminance' : 'luminance',
      WebkitMaskSize: bgMaskInvert ? `${size}, 100% 100%` : size,
      maskSize: bgMaskInvert ? `${size}, 100% 100%` : size,
      WebkitMaskPosition: bgMaskInvert ? `${pos}, 0 0` : pos,
      maskPosition: bgMaskInvert ? `${pos}, 0 0` : pos,
      // Composite to invert (XOR) when we have both mask layers.
      ...(bgMaskInvert
        ? {
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }
        : null),
    };
  }, [
    shouldUseMask,
    bgMaskUrl,
    bgMaskFit,
    bgMaskSync,
    bgMaskInvert,
    bgMaskRotate,
    rotatedMaskUrl,
    gridMetrics.gridW,
    gridMetrics.gridH,
    gridMetrics.colW,
    gridMetrics.rowH,
    elementObj?.x,
    elementObj?.y,
  ]);

  return (
    <div
      className={`relative h-full w-full ${
        elementObj?.displayCase === 'case arrondie' ? 'p-1' : ''
      }`}
    >
      <div
        className={`h-full w-full parent-hover ${
          elementObj?.displayCase === 'case arrondie'
            ? 'rounded shadow relative'
            : ''
        }`}
        style={
          bgPicUrl
            ? {
                position: 'relative',
              }
            : undefined
        }
      >
        {/* Background color layer (can be masked) */}
        <div
          className={`absolute inset-0 z-0 bg-opacity-30 bg-${elementObj?.color} ${
            elementObj?.displayCase === 'case arrondie' ? 'rounded' : ''
          }`}
          style={{
            ...(maskStyle || null),
            pointerEvents: 'none',
          }}
        />

        {/* Background image layer (can be masked + opacity) */}
        {bgPicUrl && (
          <div
            className={`absolute inset-0 z-0 ${
              elementObj?.displayCase === 'case arrondie' ? 'rounded' : ''
            }`}
            style={{
              backgroundImage: `url(${bgPicUrl})`,
              backgroundSize:
                bgPicSync && gridMetrics.gridW > 0 && gridMetrics.gridH > 0
                  ? `${gridMetrics.gridW}px ${gridMetrics.gridH}px`
                  : bgPicFit === 'stretch'
                    ? '100% 100%'
                  : bgPicFit === 'contain'
                    ? 'contain'
                    : 'cover',
              backgroundPosition: bgPicSync
                ? `${-(elementObj?.x ?? 0) * gridMetrics.colW}px ${
                    -(elementObj?.y ?? 0) * gridMetrics.rowH
                  }px`
                : 'center',
              backgroundRepeat: 'no-repeat',
              ...(maskStyle || null),
              opacity: effectiveBgOpacity,
              pointerEvents: 'none',
            }}
          />
        )}
        {isEditable(editable) && isMobile === false && (
          <TooltipHtml
            title={
              <div className='text-contrast flex items-center gap-1'>
                <div
                  className='text-secondary cursor-pointer hover:opacity-50 transition'
                  onDoubleClick={e => {
                    e.stopPropagation();
                    removeElement(getId(element));
                  }}
                >
                  <FaTimes />
                </div>
                {showDrawLayerControls && typeof onSetDrawLayer === 'function' && (
                  <>
                    <div
                      className='text-contrast cursor-pointer hover:opacity-50 transition'
                      onClick={e => {
                        e.stopPropagation();
                        onSetDrawLayer('back');
                      }}
                      title='Mettre à l’arrière-plan'
                    >
                      ↓
                    </div>
                    <div
                      className='text-contrast cursor-pointer hover:opacity-50 transition'
                      onClick={e => {
                        e.stopPropagation();
                        onSetDrawLayer('front');
                      }}
                      title='Mettre au premier plan'
                    >
                      ↑
                    </div>
                  </>
                )}
                {displayTypes && (
                  <TooltipHtml
                    title={displayTypes?.map((dtOption, i) => (
                      <div
                        key={i}
                        className={`${
                          displayType === dtOption
                            ? 'text-secondary'
                            : 'text-contrast'
                        } cursor-pointer transition hover:opacity-50 uppercase text-sm`}
                        onClick={() =>
                          updateField(
                            tab,
                            type,
                            dtOption,
                            elementObj?.i,
                            'displayType',
                          )
                        }
                      >
                        {dtOption}
                      </div>
                    ))}
                  >
                    <div className='text-contrast uppercase text-sm'>
                      {displayType}
                    </div>
                  </TooltipHtml>
                )}
                {elementObj?.type === 'filters' && (
                  <div
                    className={`cursor-pointer hover:opacity-50 transition ${
                      elementObj?.filterDragLocked === true
                        ? 'text-secondary'
                        : 'text-contrast'
                    }`}
                    title={
                      elementObj?.filterDragLocked === true
                        ? 'Déverrouiller le drag & drop du filtre'
                        : 'Verrouiller le drag & drop du filtre'
                    }
                    onClick={e => {
                      e.stopPropagation();
                      updateField(
                        tab,
                        type,
                        elementObj?.filterDragLocked !== true,
                        elementObj?.i,
                        'filterDragLocked',
                      );
                    }}
                  >
                    {elementObj?.filterDragLocked === true ? (
                      <FaLock />
                    ) : (
                      <FaLockOpen />
                    )}
                  </div>
                )}
                {elementObj?.type !== 'divider' &&
                  elementObj?.type !== 'text' &&
                  (elementObj?.type !== 'link' ||
                    (elementObj?.type === 'link' &&
                      (elementObj?.displayType === 'réduit' ||
                        elementObj?.displayType === 'complet' ||
                        elementObj?.displayType === 'complet dynamique'))) && (
                    <div
                      onClick={() => {
                        dispatch(
                          setOpenModales({
                            id:
                              elementObj?.type === 'link'
                                ? elementObj?.displayType === 'réduit'
                                  ? 'link' + getId(element)
                                  : 'linkFull' + getId(element)
                                : elementObj?.type === 'num' ||
                                    elementObj?.type === 'has_num'
                                  ? `numSettings:${elementObj?.item}:${getId(element)}`
                                : getId(element),
                            open: true,
                          }),
                        );
                      }}
                      className='cursor-pointer hover:opacity-50 transition'
                    >
                      <FaEdit />
                    </div>
                  )}
                <TooltipHtml
                  title={
                    <ColorsSwatch
                      colors={[
                        'transparent',
                        'main-d',
                        'main',
                        'primary-d',
                        'primary',
                        'secondary-d',
                        'red',
                        'orange',
                        'yellow',
                        'lime',
                        'green',
                        'blue',
                        'purple',
                        'pink',
                      ]}
                      onClick={color =>
                        updateField(tab, type, color, elementObj?.i, 'color')
                      }
                    />
                  }
                >
                  <div>
                    <FaPalette />
                  </div>
                </TooltipHtml>
                {ownerItem?.pics?.length > 0 && (
                  <TooltipHtml
                    title={
                      <div className='flex flex-wrap gap-05 p-05'>
                        <PicSelectionOptions
                          options={bgPicOptions}
                          value={elementObj?.bgPicId ?? 'none'}
                          size={32}
                          onChange={value =>
                            updateField(
                              tab,
                              type,
                              value === 'none' ? null : value,
                              elementObj?.i,
                              'bgPicId',
                            )
                          }
                          extra={
                            <div className='board-grid-no-drag'>
                              <div className='text-xs text-contrast text-opacity-70 mb-02'>
                                Opacité
                              </div>
                              <OpacitySlider
                                value={effectiveBgOpacity}
                                onChange={setBgOpacityPreview}
                                onCommit={value =>
                                  updateField(
                                    tab,
                                    type,
                                    value,
                                    elementObj?.i,
                                    'bgPicOpacity',
                                  )
                                }
                              />
                              <div className='text-xs text-contrast text-opacity-70 mb-02 mt-04'>
                                Affichage
                              </div>
                              <div className='flex gap-02 flex-wrap'>
                                {[
                                  { key: 'content', label: 'Remplir la case' },
                                  {
                                    key: 'stretch',
                                    label: 'Remplir (étiré)',
                                  },
                                  {
                                    key: 'contain',
                                    label: 'Afficher en entier',
                                  },
                                  {
                                    key: 'fixed',
                                    label: 'Continuité entre cases',
                                  },
                                ].map(option => {
                                  const isActive = bgPicFit === option.key;
                                  return (
                                    <div
                                      key={option.key}
                                      className={`cursor-pointer text-xs font-semibold px-03 py-01 rounded transition ${
                                        isActive
                                          ? 'bg-primary text-main'
                                          : 'bg-main text-contrast text-opacity-70'
                                      }`}
                                      onClick={() =>
                                        updateField(
                                          tab,
                                          type,
                                          option.key,
                                          elementObj?.i,
                                          'bgPicFit',
                                        )
                                      }
                                    >
                                      {option.label}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          }
                        />
                      </div>
                    }
                  >
                    <div className='text-contrast cursor-pointer hover:opacity-50 transition'>
                      <FaImage />
                    </div>
                  </TooltipHtml>
                )}
                <TooltipHtml
                  title={displayCases.map(displayCase => (
                    <div
                      key={displayCase}
                      className={`${
                        elementObj?.displayCase === displayCase
                          ? 'text-secondary'
                          : 'text-contrast'
                      } cursor-pointer transition hover:opacity-50 uppercase text-sm`}
                      onClick={() =>
                        updateField(
                          tab,
                          type,
                          displayCase,
                          elementObj?.i,
                          'displayCase',
                        )
                      }
                    >
                      {displayCase}
                    </div>
                  ))}
                >
                  <div className='text-contrast uppercase text-sm'>
                    {elementObj?.displayCase ?? 'case pleine largeur'}
                  </div>
                </TooltipHtml>
                {elementObj?.displayCase === 'masque' &&
                  ownerItem?.pics?.length > 0 && (
                    <TooltipHtml
                      placement='bottom'
                      title={
                        <div className='flex flex-col gap-05 p-05'>
                          <PicSelectionOptions
                            title='Masque'
                            options={maskPicOptions}
                            value={elementObj?.bgMaskPicId ?? 'none'}
                            size={32}
                            onChange={value =>
                              updateField(
                                tab,
                                type,
                                value === 'none' ? null : value,
                                elementObj?.i,
                                'bgMaskPicId',
                              )
                            }
                            extra={
                              <div className='board-grid-no-drag'>
                                <div className='text-xs text-contrast text-opacity-70 mb-02 mt-02'>
                                  Affichage du masque
                                </div>
                                <div className='flex gap-02 flex-wrap'>
                                  {[
                                    { key: 'cover', label: 'Remplir la case' },
                                    {
                                      key: 'stretch',
                                      label: 'Remplir (étiré)',
                                    },
                                    {
                                      key: 'contain',
                                      label: 'Afficher en entier',
                                    },
                                    {
                                      key: 'fixed',
                                      label: 'Continuité entre cases',
                                    },
                                  ].map(option => {
                                    const isActive = bgMaskFit === option.key;
                                    return (
                                      <div
                                        key={option.key}
                                        className={`cursor-pointer text-xs font-semibold px-03 py-01 rounded transition ${
                                          isActive
                                            ? 'bg-primary text-main'
                                            : 'bg-main text-contrast text-opacity-70'
                                        }`}
                                        onClick={e => {
                                          e.stopPropagation();
                                          updateField(
                                            tab,
                                            type,
                                            option.key,
                                            elementObj?.i,
                                            'bgMaskFit',
                                          );
                                        }}
                                      >
                                        {option.label}
                                      </div>
                                    );
                                  })}
                                </div>
                                <div
                                  className='mt-04'
                                  onMouseDownCapture={e => e.stopPropagation()}
                                  onTouchStartCapture={e => e.stopPropagation()}
                                  onPointerDownCapture={e => e.stopPropagation()}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <Checkbox
                                    label='Inverser le masque'
                                    value={bgMaskInvert}
                                    size='sm'
                                    onChange={next =>
                                      updateField(
                                        tab,
                                        type,
                                        next,
                                        elementObj?.i,
                                        'bgMaskInvert',
                                      )
                                    }
                                    className='text-xs text-contrast text-opacity-70'
                                  />
                                </div>
                                <div className='text-xs text-contrast text-opacity-70 mb-02 mt-04'>
                                  Rotation
                                </div>
                                <div className='flex gap-02 flex-wrap'>
                                  {[
                                    { key: 0, label: '0' },
                                    { key: 90, label: '+90' },
                                    { key: -90, label: '-90' },
                                    { key: 180, label: '180' },
                                  ].map(option => {
                                    const isActive =
                                      (bgMaskRotate ?? 0) === option.key;
                                    return (
                                      <div
                                        key={option.key}
                                        className={`cursor-pointer text-xs font-semibold px-03 py-01 rounded transition ${
                                          isActive
                                            ? 'bg-primary text-main'
                                            : 'bg-main text-contrast text-opacity-70'
                                        }`}
                                        onClick={e => {
                                          e.stopPropagation();
                                          updateField(
                                            tab,
                                            type,
                                            option.key,
                                            elementObj?.i,
                                            'bgMaskRotate',
                                          );
                                        }}
                                      >
                                        {option.label}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            }
                          />
                        </div>
                      }
                    >
                      <div className='text-contrast cursor-pointer hover:opacity-50 transition'>
                        <FaMoneyBillWaveAlt />
                      </div>
                    </TooltipHtml>
                  )}
              </div>
            }
          >
            <div
              data-draw-settings-handle='true'
              className={`absolute right-0 top-0 cursor-move ${
                isDrawBoard
                  ? 'z-70 opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto'
                  : 'show-on-hover z-20'
              }`}
            >
              <FaCog />
            </div>
          </TooltipHtml>
        )}
        <div
          className={`relative z-10 h-full w-full ${
            elementObj?.displayCase === 'case arrondie'
              ? 'rounded overflow-hidden'
              : 'overflow-hidden'
          }`}
        >
          {elementObj && (
            <GetField
              fieldId={getId(element)}
              fieldInGrid={elementObj}
              itemId={elementObj?.item}
              boardTabId={tab?._id}
              boardType={type}
              gridLayoutKey={gridLayoutKey}
              canEditOverride={widgetEditable}
              getTextHeight={
                hasDynamicHeight(elementObj) ? getTextHeight : null
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const BoardGridMap = React.memo(BoardGridMapComponent);
